import React, { FC, useCallback, useMemo, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import _reduce from 'lodash/reduce';
import _filter from 'lodash/filter';
import { gqlCountryEnum as COUNTRY } from '@minecraft.graphql-types';
import DropdownWithChildren from '@minecraft.dropdown-with-children';
import ErrorMessage from '@minecraft.error-message';
import { useTranslation } from '@blocs.i18n';
import { FilterData } from '../../../shared/types';
import { getLocations } from '../../../shared/utils';
import { QUERY_DMAS } from '../FilterBasicCollectionGraphQL';

type Props = FilterData;

const WorkingLocations: FC<Props> = ({ name, label }) => {
  const { t } = useTranslation();

  const [getDmas, { data, error, loading }] = useLazyQuery(QUERY_DMAS);

  const handleDmasFetch = useCallback((): void => {
    if (!data) {
      getDmas();
    }
  }, [data]);

  const workingLocationList = useMemo(() => {
    const { usLocations, caLocations, euLocations, auLocations, additionalLocations, laLocations } = getLocations({
      dmas: data?.dmas || [],
    });
    const workingLocation = [
      ...(usLocations?.length > 0
        ? [{ id: COUNTRY.US, name: t('common:label.country.unitedStates'), options: usLocations }]
        : []),
      ...(caLocations?.[0]?.cities?.length > 0
        ? [{ id: COUNTRY.CA, name: t('common:label.country.canada'), options: caLocations?.[0]?.cities }]
        : []),
      ...(euLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.GB, COUNTRY.IE]}`,
              name: t('common:label.country.unitedKingdomIreland'),
              options: euLocations,
            },
          ]
        : []),
      ...(auLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.AU, COUNTRY.NZ]}`,
              name: t('common:label.country.australiaNewZealand'),
              options: auLocations,
            },
          ]
        : []),
      ...(laLocations?.length > 0
        ? [
            {
              id: `${[COUNTRY.MX, COUNTRY.BR, COUNTRY.VE, COUNTRY.PE, COUNTRY.CL, COUNTRY.AR, COUNTRY.CO]}`,
              name: t('common:label.country.latinAmerica'),
              options: laLocations,
            },
          ]
        : []),
      ...(additionalLocations?.length > 0
        ? [{ id: 'ADDITINAL', name: t('common:label.addtionalLocations'), options: additionalLocations }]
        : []),
    ];

    const generateNestedOptions = (options) => {
      return _reduce(
        options,
        (acc, option) => {
          return [
            ...acc,
            {
              value: option.id,
              label: option.name,
              ...((option?.options || option?.cities) && {
                options: generateNestedOptions(option?.options || option?.cities),
              }),
            },
          ];
        },
        []
      );
    };

    return generateNestedOptions(_filter(workingLocation, (location) => location.options));
  }, [data?.dmas]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <DropdownWithChildren
      withFormik
      withSearch
      withSelectAll
      isMultipleSelect
      isLoading={loading}
      name={name}
      toggleLabel={label}
      options={workingLocationList}
      onOptionsFetch={handleDmasFetch}
    />
  );
};

export default memo(WorkingLocations);
